/**
 * Pure decision logic for the OverflowPrompt dialog.
 *
 * This module contains NO react / next-intl imports. It returns data — i18n
 * KEYS, CTA hrefs/labelKeys, an icon token, and a fact-line descriptor — that
 * `OverflowPrompt.tsx` renders into JSX. Keeping the per-error-code scenario
 * mapping, the `thinkingMode → mode-name` mapping, and the fact-line assembly
 * here makes them unit-testable without an RTL provider tree (next-intl +
 * Redux), per the repo's no-provider-tree test convention.
 *
 * @see GitHub issue #5524 (test gap), #5482 / PR #5519
 */

import type { QuotaErrorCode } from '@/lib/redux/slices/errorModalSlice';
import type { ThinkingMode } from '@/types/thinkingTypes';

/**
 * Lucide icon tokens. The component maps each to a concrete icon + className.
 *
 * `Lock` (error colour) and `LockInfo` (info colour) are intentionally distinct
 * tokens: TRIAL_EXPIRED uses the error-coloured lock, MODE_NOT_AVAILABLE the
 * info-coloured one — preserving the original per-scenario icon colours.
 */
export type OverflowIcon =
  | 'AlertTriangle'
  | 'Lock'
  | 'LockInfo'
  | 'Clock'
  | 'CreditCard'
  | 'Zap'
  | 'TrendingUp';

/**
 * A data-grounded fact line rendered below the description.
 *
 * - `i18n`: rendered via `t(key, params)`. `paramKeys` lets a param value be
 *   itself an i18n key (e.g. the localized mode name) — the component resolves
 *   each via `t()` and merges the result into `params` before rendering.
 * - `raw`: rendered verbatim (e.g. a backend-provided message), never translated.
 */
export type FactLine =
  | {
      kind: 'i18n';
      key: string;
      params?: Record<string, string | number>;
      /** param name → i18n key; resolved by the component and merged into params. */
      paramKeys?: Record<string, string>;
    }
  | { kind: 'raw'; text: string; italicMuted?: true };

/** A single CTA button's pure description. */
export interface ActionDescriptor {
  /** i18n key for the button label. */
  labelKey: string;
  /** When set, this i18n key supplies the label text directly (bypasses labelKey). */
  labelTextKey?: string;
  /** href for Link navigation, or null for close-only / mode-switch actions. */
  href: string | null;
  /** When set, clicking the button switches to this thinking mode. */
  switchToMode?: ThinkingMode;
}

/** The fully-resolved, pure description of an overflow scenario. */
interface ScenarioDescriptor {
  icon: OverflowIcon;
  titleKey: string;
  descriptionKey: string;
  /** Fact-line descriptor, or null when the scenario has no fact line. */
  factLine: FactLine | null;
  primaryAction: ActionDescriptor;
  secondaryAction?: ActionDescriptor;
}

/** Inputs the pure scenario resolver needs from the component. */
export interface ScenarioInputs {
  errorCode: QuotaErrorCode;
  thinkingMode: string | null;
  isPaidUser: boolean;
  fullRemaining: number | null;
  monthlyTotal: number | null;
  monthlyCap: number | null;
  /** Pre-formatted period-end string (locale formatting stays in the component). */
  monthlyPeriodEndFormatted: string | null;
  /** Live countdown string to the next daily reset, or '' when unavailable. */
  countdown: string;
  /**
   * Raw backend message, rendered verbatim ONLY for GENERIC_LIMIT — the one
   * code with no structured signal to key a localized message off (issue
   * #10336). Every other recognized code must never render this raw.
   */
  backendMessage: string | null;
  /**
   * Doc-gen monthly usage count / ceiling (DOC_GEN_MONTHLY_LIMIT_REACHED
   * only, issue #7468's `used`/`limit` fields). Locale-agnostic numbers,
   * interpolated into a localized fact line instead of the backend's raw
   * English sentence (issue #10336). Null when unavailable.
   */
  docGenUsed: number | null;
  docGenLimit: number | null;
  /**
   * True when the caller is the OWNER of the active org (JWT `org_role ===
   * 'org:owner'`, surfaced as `activeOrg.isAdmin` in auth-context.tsx).
   * Branches `ORG_SUSPENDED` copy: only the owner can resubscribe, so only
   * the owner is sent to `/organization/billing` — an ordinary member is
   * pointed at their personal account instead (issue #11315, follow-up from
   * #11147's JWT migration, which deliberately keeps the org claim for
   * owners so this page stays reachable). Ignored by every other code.
   */
  isOrgOwner: boolean;
}

const PACKS_HREF = '/pricing?scroll=packs';
const PRICING_HREF = '/pricing';
const ORG_BILLING_HREF = '/organization/billing';

/**
 * Returns the i18n key for the mode-specific "Buy X tasks" label.
 * Maps the internal `ThinkingMode` token to the corresponding `OverflowPrompt.buyMode*` key.
 */
export function getModeBuyLabelKey(thinkingMode: string | null): string {
  switch (thinkingMode) {
    case 'deep':
      return 'buyModeDeep';
    case 'thinking':
    case 'full':
      return 'buyModeFull';
    case 'fast':
      return 'buyModeStandard';
    default:
      return 'buyModeFallback';
  }
}

/**
 * Returns the i18n key for the localized display name of a thinking mode.
 * The component resolves this via `t()`.
 */
export function getModeNameKey(thinkingMode: string | null): string {
  switch (thinkingMode) {
    case 'deep':
      return 'modeDeep';
    case 'thinking':
    case 'full':
      return 'modeFull';
    case 'fast':
      return 'modeStandard';
    default:
      return 'modeUnknown';
  }
}

function resetCountdownFactLine(countdown: string): FactLine | null {
  return countdown
    ? { kind: 'i18n', key: 'factResetsIn', params: { countdown } }
    : null;
}

/**
 * Resolves the pure scenario descriptor for a given quota error code.
 *
 * Returns data only — no JSX, no translation calls. The component maps the
 * returned keys/tokens to rendered output.
 */
export function getScenarioDescriptor(
  inputs: ScenarioInputs
): ScenarioDescriptor {
  const {
    errorCode,
    thinkingMode,
    isPaidUser,
    fullRemaining,
    monthlyTotal,
    monthlyCap,
    monthlyPeriodEndFormatted,
    countdown,
    backendMessage,
    docGenUsed,
    docGenLimit,
    isOrgOwner,
  } = inputs;

  const buyLabelKey = getModeBuyLabelKey(thinkingMode);
  const genericBuyLabelKey = 'buyModeFallback';

  switch (errorCode) {
    case 'DAILY_LIMIT_REACHED': {
      const isDeep = thinkingMode === 'deep';
      const isFull = thinkingMode === 'thinking' || thinkingMode === 'full';
      const isFullAvailable =
        fullRemaining === null || (fullRemaining !== null && fullRemaining > 0);

      if (isDeep) {
        // Deep exhausted — suggest Full if available, otherwise Standard
        const fallbackMode: ThinkingMode = isFullAvailable
          ? 'thinking'
          : 'fast';
        const fallbackLabelKey = isFullAvailable ? 'useFull' : 'useStandard';

        return {
          icon: 'Zap',
          titleKey: 'dailyDeepExhaustedTitle',
          descriptionKey: isFullAvailable
            ? 'dailyDeepExhaustedDescription'
            : 'dailyDeepFullExhaustedDescription',
          factLine: resetCountdownFactLine(countdown),
          primaryAction: {
            labelKey: fallbackLabelKey,
            href: null,
            switchToMode: fallbackMode,
          },
          secondaryAction: {
            labelKey: 'buyCredits',
            labelTextKey: buyLabelKey,
            href: PACKS_HREF,
          },
        };
      }

      if (isFull) {
        return {
          icon: 'Clock',
          titleKey: 'dailyFullExhaustedTitle',
          descriptionKey: 'dailyFullExhaustedDescription',
          factLine: resetCountdownFactLine(countdown),
          primaryAction: {
            labelKey: 'useStandard',
            href: null,
            switchToMode: 'fast',
          },
          secondaryAction: {
            labelKey: 'buyCredits',
            labelTextKey: buyLabelKey,
            href: PACKS_HREF,
          },
        };
      }

      // Standard mode daily limit (trial users only)
      return {
        icon: 'AlertTriangle',
        titleKey: 'dailyLimitTitle',
        descriptionKey: 'dailyLimitDescription',
        factLine: resetCountdownFactLine(countdown),
        primaryAction: {
          labelKey: 'buyCredits',
          labelTextKey: buyLabelKey,
          href: PACKS_HREF,
        },
        secondaryAction: { labelKey: 'upgrade', href: PRICING_HREF },
      };
    }

    case 'MONTHLY_CAP_REACHED': {
      const factLine: FactLine | null =
        monthlyTotal !== null && monthlyCap !== null
          ? {
              kind: 'i18n',
              key: 'factMonthlyCap',
              params: {
                total: monthlyTotal,
                cap: monthlyCap,
                periodEnd: monthlyPeriodEndFormatted ?? '',
              },
            }
          : null;

      return {
        icon: 'TrendingUp',
        titleKey: 'monthlyCapTitle',
        descriptionKey: 'monthlyCapDescription',
        factLine,
        primaryAction: {
          labelKey: 'buyCredits',
          labelTextKey: genericBuyLabelKey,
          href: PACKS_HREF,
        },
        secondaryAction: { labelKey: 'upgrade', href: PRICING_HREF },
      };
    }

    case 'TRIAL_EXPIRED':
      return {
        icon: 'Lock',
        titleKey: 'trialExpiredTitle',
        descriptionKey: 'trialExpiredDescription',
        factLine: { kind: 'i18n', key: 'factTrialExpiredAvailable' },
        primaryAction: { labelKey: 'subscribeTrial', href: PRICING_HREF },
      };

    case 'MODE_NOT_AVAILABLE':
      return {
        icon: 'LockInfo',
        titleKey: 'modeNotAvailableTitle',
        descriptionKey: isPaidUser
          ? 'modeNotAvailablePaidDescription'
          : 'modeNotAvailableDescription',
        factLine: !isPaidUser
          ? {
              kind: 'i18n',
              key: 'factModeNotAvailable',
              paramKeys: { mode: getModeNameKey(thinkingMode) },
            }
          : null,
        primaryAction: { labelKey: 'upgrade', href: PRICING_HREF },
      };

    case 'INSUFFICIENT_CREDITS':
      return {
        icon: 'CreditCard',
        titleKey: 'insufficientCreditsTitle',
        descriptionKey: 'insufficientCreditsDescription',
        factLine: null,
        primaryAction: {
          labelKey: 'buyCredits',
          labelTextKey: buyLabelKey,
          href: PACKS_HREF,
        },
      };

    case 'DOC_GEN_MONTHLY_LIMIT_REACHED':
      // Per-tier monthly document-generation cap (issue #7468). A MONTHLY
      // plan restriction resolved only by upgrading: no daily-reset countdown
      // and no PAYG action (doc-gen has no PAYG path).
      //
      // The fact line renders the used/limit counts via a localized i18n key
      // — NOT the backend's raw English `message` string (issue #10336: this
      // is a RECOGNIZED code, so it must never leak untranslated backend
      // text the way the GENERIC_LIMIT fallback deliberately does). `limit`
      // of 0 means doc-gen isn't included in the plan at all ("0/0" would be
      // a meaningless fact line), so the numeric fact line only renders when
      // there's a real ceiling to report; the localized title/description
      // above already cover the zero-limit case.
      return {
        icon: 'Lock',
        titleKey: 'docGenMonthlyCapTitle',
        descriptionKey: 'docGenMonthlyCapDescription',
        factLine:
          docGenUsed !== null && docGenLimit !== null && docGenLimit > 0
            ? {
                kind: 'i18n',
                key: 'factDocGenMonthlyCap',
                params: { used: docGenUsed, limit: docGenLimit },
              }
            : null,
        primaryAction: { labelKey: 'upgrade', href: PRICING_HREF },
      };

    case 'ORG_SUSPENDED':
      // The org finished onboarding and its Stripe subscription has since
      // ended (backend quota_types.py / #2544). Only the owner can act on
      // it — an ordinary member is pointed at their personal account
      // instead, per #11315.
      return isOrgOwner
        ? {
            icon: 'Lock',
            titleKey: 'orgSuspendedTitle',
            descriptionKey: 'orgSuspendedOwnerDescription',
            factLine: null,
            primaryAction: {
              labelKey: 'renewOrgSubscription',
              href: ORG_BILLING_HREF,
            },
          }
        : {
            icon: 'Lock',
            titleKey: 'orgSuspendedTitle',
            descriptionKey: 'orgSuspendedMemberDescription',
            factLine: null,
            primaryAction: { labelKey: 'gotIt', href: null },
          };

    case 'ORG_NOT_ACTIVE':
      // The org has not finished onboarding, or does not exist yet (backend
      // quota_types.py). Same single message for owner and member — neither
      // role has a page to jump to mid-onboarding (unlike ORG_SUSPENDED's
      // /organization/billing).
      return {
        icon: 'LockInfo',
        titleKey: 'orgNotActiveTitle',
        descriptionKey: 'orgNotActiveDescription',
        factLine: null,
        primaryAction: { labelKey: 'gotIt', href: null },
      };

    case 'GENERIC_LIMIT':
      return {
        icon: 'AlertTriangle',
        titleKey: 'genericLimitTitle',
        descriptionKey: 'genericLimitDescription',
        factLine: backendMessage
          ? { kind: 'raw', text: backendMessage, italicMuted: true }
          : null,
        primaryAction: {
          labelKey: 'buyCredits',
          labelTextKey: genericBuyLabelKey,
          href: PACKS_HREF,
        },
        secondaryAction: { labelKey: 'upgrade', href: PRICING_HREF },
      };
  }
}
